import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';

import { ConnectionStatus } from '@pflegia/types/common/enums/connection-status.enum';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { Match } from '@pflegia/types/common/match/match';

import {
  JOBS_FOR_EMPLOYER_PAGE_SIZE,
  PositionRelatedJobsFilters,
  useGetOtherJobsForEmployer,
} from '../application/use-get-other-jobs-for-employer';

interface JobDetailsContextType {
  employerId?: string;
  jobPositionId?: string;
  // OtherJobsForEmployer
  relatedJobs: ReturnType<typeof useGetOtherJobsForEmployer>;
  // State of the match used in the Offer Modal
  matchDetails: ConnectionInfo;
  isJobPositionPaused: boolean;
  isInitiative: boolean;
  setMatchDetails: Dispatch<SetStateAction<ConnectionInfo>>;
  onMatchUpdate: (match: Match) => Promise<void>;
  preventApplicationForPausedPositions: (
    isJobPaused: boolean,
    isPreview: boolean,
    isExternalJob: boolean,
    isJobDetailsPage: boolean,
  ) => boolean;
  showPausedModal: boolean;
  setShowPausedModal: Dispatch<SetStateAction<boolean>>;
}

export const JobDetailsContext = createContext<JobDetailsContextType | null>(null);

interface ProviderProps {
  children: React.ReactNode;
  employerId?: string;
  jobPositionId?: string;
  populateEmployerRelatedJobs?: boolean;
  positionRelatedJobsFilters?: PositionRelatedJobsFilters;
  isJobPositionPaused?: boolean;
  isInitiative?: boolean;
}

/**
 * JobDetailsProvider component provides job details context to its children.
 *
 * @param {React.ReactNode} children - The child components.
 * @param {string} employerId - The ID of the employer.
 * @param {string} [jobPositionId] - Optional ID of the job position.
 */
export const JobDetailsProvider: React.FC<ProviderProps> = ({
  children,
  employerId,
  jobPositionId,
  populateEmployerRelatedJobs = false,
  positionRelatedJobsFilters,
  isJobPositionPaused = false,
  isInitiative = false,
}) => {
  // Fetch related jobs for the given employer and job position
  const relatedJobs = useGetOtherJobsForEmployer({ employerId, jobPositionId, populateEmployerRelatedJobs, positionRelatedJobsFilters });

  // State to manage match details for the offer modal (to replace the drilled down connectionDetails state)
  const [matchDetails, setMatchDetails] = useState<ConnectionInfo>({
    status: ConnectionStatus.Pending,
    type: 'default',
    jobPositionId: jobPositionId ?? '',
    jobPositionTitle: '',
    matchId: '',
  });

  // Destructure jobs and refetch function from relatedJobs
  const { jobs, refetch } = relatedJobs;

  /**
   * Callback to update the match and refetch related jobs if necessary.
   */
  const onMatchUpdate = useCallback(
    async (match: Match) => {
      const page = Math.max(
        Math.floor(jobs?.findIndex(({ jobPosition: { id } }) => id === match.jobPositionId) / JOBS_FOR_EMPLOYER_PAGE_SIZE),
        0,
      );
      await refetch({ page });
    },
    [jobs, refetch],
  );

  const [showPausedModal, setShowPausedModal] = useState(false);

  const preventApplicationForPausedPositions = useCallback(
    (isJobPaused: boolean, isPreview: boolean, isExternalJob: boolean, isJobDetailsPage: boolean) => {
      const shouldPrevent = isJobPaused && !isPreview && !isExternalJob && isJobDetailsPage && !isInitiative;

      if (shouldPrevent) {
        setShowPausedModal(true);
        return true;
      }
      return false;
    },
    [isInitiative],
  );

  const contextValue = useMemo(
    () => ({
      employerId,
      jobPositionId,
      relatedJobs,
      matchDetails,
      isJobPositionPaused,
      setMatchDetails,
      onMatchUpdate,
      preventApplicationForPausedPositions,
      showPausedModal,
      setShowPausedModal,
      isInitiative,
    }),
    [
      employerId,
      jobPositionId,
      relatedJobs,
      matchDetails,
      isJobPositionPaused,
      setMatchDetails,
      onMatchUpdate,
      preventApplicationForPausedPositions,
      showPausedModal,
      setShowPausedModal,
      isInitiative,
    ],
  );

  return <JobDetailsContext.Provider value={contextValue}>{children}</JobDetailsContext.Provider>;
};

export const useJobDetails = () => {
  const context = useContext(JobDetailsContext);
  if (!context) throw Error('Access hook without initialization');
  return context;
};
